import React from 'react';
import { Modal, Form, Input, InputNumber, Select, DatePicker, Button, Row, Col, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useUploadDocumentMutation } from '../../store/api/documentsApi';
import { formatApplicationSelectOptionLabel } from '../../utils/applicationDisplayFormat';
import { useGetApplicationsQuery } from '../../store/api/applicationsApi';
import type { UploadFile } from 'antd/es/upload/interface';

interface CreateClientInvoiceModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  /** Предвыбранная заявка (при открытии из карточки заявки) */
  preselectedApplicationId?: number;
}

const CreateClientInvoiceModal: React.FC<CreateClientInvoiceModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  preselectedApplicationId
}) => {
  const [form] = Form.useForm();
  const [uploadDocument, { isLoading }] = useUploadDocumentMutation();
  const [fileList, setFileList] = React.useState<UploadFile[]>([]);

  const { data: applicationsData } = useGetApplicationsQuery(
    { page: 1, limit: 500, statusGroup: 'in_work' },
    { skip: !visible }
  );
  const applications = applicationsData?.applications ?? [];

  React.useEffect(() => {
    if (visible && preselectedApplicationId) {
      form.setFieldsValue({ applicationId: preselectedApplicationId });
    }
  }, [visible, preselectedApplicationId, form]);

  const buildPayload = (values: any) => {
    const applicationId = values.applicationId;
    let files = fileList.map((f) => f.originFileObj).filter(Boolean) as File[];

    if (files.length === 0) {
      const placeholderContent = `Счет №${values.invoiceNumber} от ${dayjs(values.invoiceDate).format('DD.MM.YYYY')}. Сумма: ${values.invoiceAmount} руб.`;
      const blob = new Blob([placeholderContent], { type: 'text/plain;charset=utf-8' });
      files = [new File([blob], `invoice-${values.invoiceNumber}.txt`, { type: 'text/plain' })];
    }

    return {
      applicationId: Number(applicationId),
      documentType: 'INVOICE' as const,
      fileType: 'file' as const,
      stage: 'other' as const,
      invoiceNumber: String(values.invoiceNumber).trim(),
      invoiceDate: dayjs(values.invoiceDate).toISOString(),
      invoiceAmount: Number(values.invoiceAmount),
      receivedDate: dayjs(values.invoiceDate).toISOString(),
      dueDate: values.dueDate ? dayjs(values.dueDate).toISOString() : undefined,
      files,
      originalName: files[0]?.name
    };
  };

  const handleSubmit = async (values: any) => {
    const applicationId = values.applicationId;
    if (!applicationId) {
      message.error('Выберите заявку');
      return;
    }

    try {
      await uploadDocument(buildPayload(values)).unwrap();
      message.success('Счёт клиенту успешно создан');
      form.resetFields();
      setFileList([]);
      onSuccess?.();
      onCancel();
    } catch (err: any) {
      const code = err?.data?.code;
      const st = err?.status;
      if (st === 409 && (code === 'INVOICE_DUPLICATE' || code === 'DUPLICATE_INVOICE')) {
        message.error('Счёт с такой суммой по этой заявке уже существует');
        return;
      }
      const msg = err?.data?.error || err?.error || err?.message || 'Ошибка при создании счёта';
      message.error(msg);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    onCancel();
  };

  return (
    <Modal
      title="Создать счёт клиенту"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={520}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          invoiceDate: dayjs()
        }}
      >
        <Form.Item
          name="applicationId"
          label="Заявка"
          rules={[{ required: true, message: 'Выберите заявку' }]}
        >
          <Select
            placeholder="Выберите заявку"
            showSearch
            optionFilterProp="children"
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            options={applications.map((a) => ({
              value: a.id,
              label: formatApplicationSelectOptionLabel(a)
            }))}
            disabled={!!preselectedApplicationId}
          />
        </Form.Item>

        <Form.Item name="invoiceNumber" label="Номер счёта" rules={[{ required: true, message: 'Укажите номер счёта' }]}>
          <Input placeholder="Введите номер счёта" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="invoiceDate"
              label="Дата счёта"
              rules={[{ required: true, message: 'Укажите дату счёта' }]}
            >
              <DatePicker style={{ width: '100%' }} placeholder="Дата счёта" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="invoiceAmount"
              label="Сумма"
              rules={[
                { required: true, message: 'Укажите сумму' },
                { type: 'number', min: 0.01, message: 'Сумма должна быть больше 0' }
              ]}
            >
              <InputNumber min={0.01} step={0.01} placeholder="Сумма с НДС" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="dueDate" label="Срок оплаты (опционально)">
          <DatePicker style={{ width: '100%' }} placeholder="Срок оплаты" />
        </Form.Item>

        <Form.Item label="Файл (опционально)">
          <Upload
            beforeUpload={() => false}
            fileList={fileList}
            onChange={({ fileList: fl }) => setFileList(fl)}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Выбрать файл</Button>
          </Upload>
        </Form.Item>

        <Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Button type="default" onClick={handleCancel} style={{ width: '100%' }}>
                Отмена
              </Button>
            </Col>
            <Col span={12}>
              <Button type="primary" htmlType="submit" loading={isLoading} style={{ width: '100%' }}>
                Создать счёт
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateClientInvoiceModal;
